import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormTimeField } from "src/components/common/forms/FormTimeField.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { spanGridColumns } from "src/components/common/forms/styles.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { requireRule } from "src/components/common/forms/validation.ts";
import { SalesLead } from "src/api/generated/erp/db/types/tables/salesLead.ts";
import { SalesLeadActivity } from "src/api/generated/erp/db/types/tables/salesLeadActivity.ts";
import { getSalesLeadActivityPriorityLabels } from "src/api/generated/erp/db/types/enums/salesLeadActivityPriority.ts";
import {
	SalesLeadActivitiesApi,
	SalesLeadActivitiesApi_FormInitData,
} from "src/api/generated/erp/sales/crm/salesLeadActivities/api/salesLeadActivitiesApi.ts";
import i18n from "i18next";
import { DeepPartial } from "react-hook-form";
import { IsoDateString, IsoTimeString } from "src/types/dateTime.ts";
import { SALES_LEAD_ACTIVITY_FORM_DIALOG_TITLE } from "src/components/views/erp/sales/crm/salesLeadActivities/SalesLeadActivityFormDialog.utils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {
	resolveSalesLeadActivityDue,
	SalesLeadActivityLocalDue,
	toSalesLeadActivityLocalDue,
} from "src/components/views/erp/sales/crm/salesLeadActivities/salesLeadActivityDue.ts";

/**
 * The stored due of an existing activity is kept alongside the edited local due, so that it is preserved when the
 * deadline is not changed.
 */
type SalesLeadActivityFormValues = SalesLeadActivity & SalesLeadActivityLocalDue;

export interface SalesLeadActivityFormProps extends FormCommonProps<number> {
	salesLeadActivityId?: number;
	initialSalesLeadId?: number;
	initialDueDate?: IsoDateString;
	initialDueTime?: IsoTimeString | null;
}

export const SalesLeadActivityForm = ({
	salesLeadActivityId,
	initialSalesLeadId,
	initialDueDate,
	initialDueTime,
	onCompleted,
	onFormEdited,
}: SalesLeadActivityFormProps) => {
	return (
		<AsyncForm<SalesLeadActivitiesApi_FormInitData, SalesLeadActivityFormValues, number>
			fetch={() => SalesLeadActivitiesApi.getFormInitData({ salesLeadActivityId })}
			getDefaultValues={getDefaultValues}
			submit={submit}
			columns={2}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.crm.salesLeadActivity.form"),
				title: SALES_LEAD_ACTIVITY_FORM_DIALOG_TITLE,
				data: {
					salesLeadActivityId: salesLeadActivityId ?? null,
					initialSalesLeadId: initialSalesLeadId ?? null,
					initialDueDate: initialDueDate ?? null,
					initialDueTime: initialDueTime ?? null,
				},
			}}
			render={(contentParams) => <FormContent {...contentParams} salesLeadActivityId={salesLeadActivityId} />}
		/>
	);

	function getDefaultValues(data: SalesLeadActivitiesApi_FormInitData): DeepPartial<SalesLeadActivityFormValues> {
		if (data.salesLeadActivity == null) {
			return {
				...DEFAULT_VALUES,
				salesLeadId: initialSalesLeadId,
				dueDate: initialDueDate,
				dueTime: initialDueTime ?? null,
			};
		}
		return { ...data.salesLeadActivity, ...toSalesLeadActivityLocalDue(data.salesLeadActivity) };
	}

	async function submit({ dueDate, dueTime, ...values }: SalesLeadActivityFormValues): Promise<number> {
		const currentDue = values.salesLeadActivityId == null ? null : { dueAt: values.dueAt, allDay: values.allDay };
		const normalizedValues: SalesLeadActivity = {
			...values,
			...resolveSalesLeadActivityDue({ dueDate, dueTime }, currentDue),
			title: values.title.trim(),
		};

		if (values.salesLeadActivityId == null) {
			return await SalesLeadActivitiesApi.insert({ activity: normalizedValues });
		} else {
			await SalesLeadActivitiesApi.update({
				activity: {
					...normalizedValues,
					salesLeadActivityId: normalizedValues.salesLeadActivityId,
				},
			});
			return normalizedValues.salesLeadActivityId;
		}
	}
};

interface FormContentProps extends AsyncFormContentParams<
	SalesLeadActivitiesApi_FormInitData,
	SalesLeadActivityFormValues
> {
	salesLeadActivityId?: number;
}

const FormContent = ({ control, data, salesLeadActivityId }: FormContentProps) => {
	const isCreateMode = salesLeadActivityId == null;

	return (
		<>
			<FormTextField
				control={control}
				name={"title"}
				label={i18n.t("title")}
				rules={{
					...requireRule(),
					validate: (value) => value.trim().length > 0 || i18n.t("required"),
				}}
				autoFocus
			/>
			{isCreateMode && (
				<FormAsyncSelectField
					control={control}
					name={"salesLeadId"}
					label={i18n.t("sales_lead")}
					fetchOptions={({ currentSelection, searchQuery }) =>
						SalesLeadActivitiesApi.getSalesLeadSelectionOptions({
							currentSelection,
							searchQuery,
						})
					}
					getOptionKey={(option: SalesLead) => option.salesLeadId}
					getOptionLabel={(option) => option.customerName}
					rules={requireRule()}
				/>
			)}
			<FormDateField
				control={control}
				name={"dueDate"}
				label={i18n.t("deadline")}
				rules={requireRule()}
				startNewGridRow
			/>
			<FormTimeField
				control={control}
				name={"dueTime"}
				label={i18n.t("time")}
				slotProps={{ actionBar: { actions: ["clear", "cancel", "accept"] } }}
			/>
			<FormEnumSelectField
				control={control}
				name={"priority"}
				label={i18n.t("priority")}
				options={getSalesLeadActivityPriorityLabels()}
			/>
			<FormSelectField
				control={control}
				name={"salesLeadActivityCategoryId"}
				label={i18n.t("category")}
				options={data.categoryOptions}
				getOptionKey={(option) => option.salesLeadActivityCategoryId}
				getOptionLabel={(option) => option.name}
			/>
			<FormTextField
				control={control}
				name={"description"}
				label={i18n.t("description")}
				multiline
				sx={spanGridColumns}
			/>
		</>
	);
};

const DEFAULT_VALUES: DeepPartial<SalesLeadActivityFormValues> = {
	title: "",
	description: "",
	priority: "NORMAL",
};
